import { useState, useCallback } from 'react'
import {
  useTenantPimSettings,
  useUpdateTenantPimSettings,
  useResetTenantPimSettings,
  PimSettings,
  PimSettingsResponse,
  SettingSource,
  SignalBandThresholds,
  PIM_RANGES,
  SETTING_LABELS,
  SIGNAL_NAMES,
  SIGNAL_LABELS,
  MODULE_LABELS,
} from '@/hooks/usePimSettings'
import { useAdminTenants } from '@/hooks/useAdminTenants'

type SectionKey = 'banding' | 'temporal' | 'sensitivity' | 'nudges' | 'modules'

export default function PimSettingsManager() {
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [expandedSection, setExpandedSection] = useState<SectionKey | null>('banding')
  const [expandedSignal, setExpandedSignal] = useState<string | null>(null)
  const [pendingChanges, setPendingChanges] = useState<Partial<PimSettings>>({})
  const [saveMessage, setSaveMessage] = useState<string>('')

  const { data: tenantsData } = useAdminTenants({ status: 'active' })
  const { data: settingsData, isLoading, error } = useTenantPimSettings(selectedTenantId || undefined)
  const updateMutation = useUpdateTenantPimSettings(selectedTenantId || undefined)
  const resetMutation = useResetTenantPimSettings(selectedTenantId || undefined)

  const getSource = useCallback((path: string): SettingSource => {
    return settingsData?.sources?.[path] || 'default'
  }, [settingsData])

  const sourceColor = (source: SettingSource) => {
    switch (source) {
      case 'tenant': return 'text-blue-600 bg-blue-50'
      case 'user': return 'text-purple-600 bg-purple-50'
      default: return 'text-gray-500 bg-gray-50'
    }
  }

  const sourceLabel = (source: SettingSource) => {
    switch (source) {
      case 'tenant': return 'Tenant'
      case 'user': return 'User'
      default: return 'Default'
    }
  }

  const handleSave = async () => {
    if (!selectedTenantId || Object.keys(pendingChanges).length === 0) return
    try {
      await updateMutation.mutateAsync(pendingChanges)
      setPendingChanges({})
      setSaveMessage('Settings saved successfully')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (err: any) {
      setSaveMessage(`Error: ${err.response?.data?.detail || 'Failed to save'}`)
    }
  }

  const handleReset = async () => {
    if (!selectedTenantId) return
    if (!confirm('Reset all PIM settings for this tenant to system defaults?')) return
    try {
      await resetMutation.mutateAsync()
      setPendingChanges({})
      setSaveMessage('Settings reset to defaults')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (err: any) {
      setSaveMessage(`Error: ${err.response?.data?.detail || 'Failed to reset'}`)
    }
  }

  const updateField = (section: string, field: string, value: number | boolean, subSection?: string) => {
    setPendingChanges(prev => {
      const updated = { ...prev } as any
      if (!updated[section]) updated[section] = {}
      if (subSection) {
        if (!updated[section][subSection]) updated[section][subSection] = {}
        updated[section][subSection][field] = value
      } else {
        updated[section][field] = value
      }
      return updated
    })
  }

  const getEffectiveValue = (section: string, field: string, subSection?: string): any => {
    // Check pending changes first
    const pending = pendingChanges as any
    if (subSection) {
      if (pending?.[section]?.[subSection]?.[field] !== undefined) return pending[section][subSection][field]
    } else {
      if (pending?.[section]?.[field] !== undefined) return pending[section][field]
    }
    // Fall back to server state
    const settings = settingsData?.settings as any
    if (!settings) return undefined
    if (subSection) return settings[section]?.[subSection]?.[field]
    return settings[section]?.[field]
  }

  const sections: { key: SectionKey; label: string; icon: string }[] = [
    { key: 'banding', label: 'Signal Banding Thresholds', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { key: 'temporal', label: 'Temporal Windows', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { key: 'sensitivity', label: 'Signal Sensitivity', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
    { key: 'nudges', label: 'Coaching Nudges', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
    { key: 'modules', label: 'Module Toggles', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z' },
  ]

  return (
    <div className="space-y-6">
      {/* Tenant Picker */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700">Tenant:</label>
        <select
          value={selectedTenantId}
          onChange={(e) => {
            setSelectedTenantId(e.target.value)
            setPendingChanges({})
          }}
          className="input w-72"
        >
          <option value="">Select a tenant...</option>
          {tenantsData?.tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {settingsData?.tenant_name && (
          <span className="text-sm text-gray-500">({settingsData.tenant_name})</span>
        )}
      </div>

      {!selectedTenantId && (
        <div className="card p-8 text-center text-gray-500">
          Select a tenant to manage their PIM settings
        </div>
      )}

      {isLoading && <div className="card p-8 text-center text-gray-500">Loading settings...</div>}
      {error && <div className="card p-6 bg-red-50 text-red-700">Failed to load PIM settings</div>}

      {selectedTenantId && settingsData && (
        <>
          {/* Save / Reset Bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={Object.keys(pendingChanges).length === 0 || updateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-personic-600 rounded-lg hover:bg-personic-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleReset}
                disabled={resetMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Reset to Defaults
              </button>
              {Object.keys(pendingChanges).length > 0 && (
                <span className="text-sm text-amber-600">Unsaved changes</span>
              )}
            </div>
            {saveMessage && (
              <span className={`text-sm ${saveMessage.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                {saveMessage}
              </span>
            )}
          </div>

          {/* Settings Sections */}
          <div className="space-y-3">
            {sections.map(({ key, label, icon }) => (
              <div key={key} className="card overflow-hidden">
                <button
                  onClick={() => setExpandedSection(expandedSection === key ? null : key)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <svg className="h-5 w-5 text-personic-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                    </svg>
                    <span className="font-medium text-gray-900">{label}</span>
                  </div>
                  <svg
                    className={`h-5 w-5 text-gray-400 transition-transform ${expandedSection === key ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedSection === key && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    {key === 'banding' && (
                      <BandingSection
                        settings={settingsData}
                        getSource={getSource}
                        sourceColor={sourceColor}
                        sourceLabel={sourceLabel}
                        getEffectiveValue={getEffectiveValue}
                        updateField={updateField}
                        expandedSignal={expandedSignal}
                        setExpandedSignal={setExpandedSignal}
                      />
                    )}
                    {key === 'temporal' && (
                      <NumericSection
                        section="temporal"
                        ranges={PIM_RANGES.temporal}
                        labels={SETTING_LABELS.temporal}
                        getSource={getSource}
                        sourceColor={sourceColor}
                        sourceLabel={sourceLabel}
                        getEffectiveValue={getEffectiveValue}
                        updateField={updateField}
                      />
                    )}
                    {key === 'sensitivity' && (
                      <NumericSection
                        section="sensitivity"
                        ranges={PIM_RANGES.sensitivity}
                        labels={SETTING_LABELS.sensitivity}
                        getSource={getSource}
                        sourceColor={sourceColor}
                        sourceLabel={sourceLabel}
                        getEffectiveValue={getEffectiveValue}
                        updateField={updateField}
                      />
                    )}
                    {key === 'nudges' && (
                      <NumericSection
                        section="nudges"
                        ranges={PIM_RANGES.nudges}
                        labels={SETTING_LABELS.nudges}
                        getSource={getSource}
                        sourceColor={sourceColor}
                        sourceLabel={sourceLabel}
                        getEffectiveValue={getEffectiveValue}
                        updateField={updateField}
                      />
                    )}
                    {key === 'modules' && (
                      <ModulesSection
                        getSource={getSource}
                        sourceColor={sourceColor}
                        sourceLabel={sourceLabel}
                        getEffectiveValue={getEffectiveValue}
                        updateField={updateField}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}


// ─── Banding Section ────────────────────────────────────────────────

function BandingSection({
  settings: _settings, getSource, sourceColor, sourceLabel, getEffectiveValue, updateField,
  expandedSignal, setExpandedSignal,
}: {
  settings: PimSettingsResponse
  getSource: (path: string) => SettingSource
  sourceColor: (s: SettingSource) => string
  sourceLabel: (s: SettingSource) => string
  getEffectiveValue: (section: string, field: string, sub?: string) => any
  updateField: (section: string, field: string, value: number | boolean, sub?: string) => void
  expandedSignal: string | null
  setExpandedSignal: (s: string | null) => void
}) {
  return (
    <div className="pt-4 space-y-3">
      <p className="text-sm text-gray-500 mb-4">
        Configure per-signal banding thresholds. Each signal module has independent cutoffs
        that determine how raw scores map to semantic bands (low, moderate-low, moderate, moderate-high, high).
      </p>

      {SIGNAL_NAMES.map((signal) => {
        const isExpanded = expandedSignal === signal
        return (
          <div key={signal} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => setExpandedSignal(isExpanded ? null : signal)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-800">{SIGNAL_LABELS[signal]}</span>
                <BandPreview thresholds={{
                  low: getEffectiveValue('banding', 'low', signal) ?? 0.35,
                  moderate_low: getEffectiveValue('banding', 'moderate_low', signal) ?? 0.45,
                  moderate_high: getEffectiveValue('banding', 'moderate_high', signal) ?? 0.65,
                  high: getEffectiveValue('banding', 'high', signal) ?? 0.75,
                }} />
              </div>
              <svg
                className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                {(['low', 'moderate_low', 'moderate_high', 'high'] as const).map((field) => {
                  const value = getEffectiveValue('banding', field, signal) ?? 0.5
                  const source = getSource(`banding.${signal}.${field}`)
                  return (
                    <div key={field} className="flex items-center gap-4">
                      <label className="text-sm text-gray-600 w-40">
                        {PIM_RANGES.banding[field] && (
                          field.replace('_', '-')
                        )}
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={value}
                        onChange={(e) => updateField('banding', field, parseFloat(e.target.value), signal)}
                        className="flex-1 accent-personic-600"
                      />
                      <span className="text-sm font-mono text-gray-700 w-12 text-right">{value.toFixed(2)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${sourceColor(source)}`}>
                        {sourceLabel(source)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}


// ─── Band Preview (visual bar) ──────────────────────────────────────

function BandPreview({ thresholds }: { thresholds: SignalBandThresholds }) {
  return (
    <div className="flex items-center gap-0.5 ml-3">
      <div
        className="h-2 bg-red-300 rounded-l"
        style={{ width: `${thresholds.low * 80}px` }}
        title={`Low: 0-${thresholds.low}`}
      />
      <div
        className="h-2 bg-orange-300"
        style={{ width: `${(thresholds.moderate_low - thresholds.low) * 80}px` }}
        title={`Mod-Low: ${thresholds.low}-${thresholds.moderate_low}`}
      />
      <div
        className="h-2 bg-yellow-300"
        style={{ width: `${(thresholds.moderate_high - thresholds.moderate_low) * 80}px` }}
        title={`Moderate: ${thresholds.moderate_low}-${thresholds.moderate_high}`}
      />
      <div
        className="h-2 bg-emerald-300"
        style={{ width: `${(thresholds.high - thresholds.moderate_high) * 80}px` }}
        title={`Mod-High: ${thresholds.moderate_high}-${thresholds.high}`}
      />
      <div
        className="h-2 bg-green-400 rounded-r"
        style={{ width: `${(1 - thresholds.high) * 80}px` }}
        title={`High: ${thresholds.high}-1.0`}
      />
    </div>
  )
}


// ─── Generic Numeric Section ────────────────────────────────────────

function NumericSection({
  section, ranges, labels, getSource, sourceColor, sourceLabel, getEffectiveValue, updateField,
}: {
  section: string
  ranges: Record<string, { min: number; max: number; step: number }>
  labels: Record<string, string>
  getSource: (path: string) => SettingSource
  sourceColor: (s: SettingSource) => string
  sourceLabel: (s: SettingSource) => string
  getEffectiveValue: (section: string, field: string, sub?: string) => any
  updateField: (section: string, field: string, value: number | boolean, sub?: string) => void
}) {
  return (
    <div className="pt-4 space-y-4">
      {Object.entries(ranges).map(([field, range]) => {
        const value = getEffectiveValue(section, field) ?? range.min
        const source = getSource(`${section}.${field}`)
        return (
          <div key={field} className="flex items-center gap-4">
            <label className="text-sm text-gray-600 w-56">{labels[field] || field}</label>
            <input
              type="range"
              min={range.min}
              max={range.max}
              step={range.step}
              value={value}
              onChange={(e) => updateField(section, field, parseFloat(e.target.value))}
              className="flex-1 accent-personic-600"
            />
            <span className="text-sm font-mono text-gray-700 w-14 text-right">
              {Number.isInteger(range.step) ? value : value.toFixed(2)}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded ${sourceColor(source)}`}>
              {sourceLabel(source)}
            </span>
          </div>
        )
      })}
    </div>
  )
}


// ─── Modules Section ────────────────────────────────────────────────

function ModulesSection({
  getSource, sourceColor, sourceLabel, getEffectiveValue, updateField,
}: {
  getSource: (path: string) => SettingSource
  sourceColor: (s: SettingSource) => string
  sourceLabel: (s: SettingSource) => string
  getEffectiveValue: (section: string, field: string, sub?: string) => any
  updateField: (section: string, field: string, value: number | boolean, sub?: string) => void
}) {
  return (
    <div className="pt-4 space-y-3">
      <p className="text-sm text-gray-500 mb-4">
        Enable or disable individual coaching modules for this tenant's users.
      </p>
      {Object.entries(MODULE_LABELS).map(([key, label]) => {
        const enabled = getEffectiveValue('modules', key) ?? true
        const source = getSource(`modules.${key}`)
        return (
          <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <button
                onClick={() => updateField('modules', key, !enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  enabled ? 'bg-personic-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm font-medium text-gray-800">{label}</span>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded ${sourceColor(source)}`}>
              {sourceLabel(source)}
            </span>
          </div>
        )
      })}
    </div>
  )
}
